import { useState } from "react";
import { FaEdit, FaPrint} from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { HiXMark } from "react-icons/hi2";
import { PrintLayout } from "../PrintLayout";
import ConfirmActionModal from "../ConfirmDeleteModal";
import { format } from "date-fns";

const ViewReturnModal = ({ isOpen, onClose, returnData, onEdit, onDelete, type, formatBillNumber }) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  if (!isOpen || !returnData) return null;

  const {
    id,
    billNumber,
    customerName,
    returnedAt,
    note,
    totalAmount = 0,
    adjustmentType,
    items = [],
  } = returnData;


  const returnItems =
    items.length > 0
      ? items.map((item, index) => {
          const salePriceNum = +item.salePrice || +item.unitPrice || 0;
          const taxNum = +item.tax || 0;
          const quantityNum = +item.quantity || +item.returnQty || 0;

          if (isNaN(salePriceNum) || isNaN(taxNum) || isNaN(quantityNum)) {
            console.warn(`Invalid numeric value in item ${index}:`, item);
          }

          return {
            ...item,
            medicineName: item.medicineName || "N/A",
            quantity: quantityNum,
            salePrice: salePriceNum,
            tax: taxNum,
            total: (quantityNum * salePriceNum * (1 + taxNum / 100)).toFixed(2),
          };
        })
      : [];

  const subTotal = returnItems.reduce(
    (sum, item) => sum + item.quantity * item.salePrice,
    0
  );

  const taxAmount = returnItems.reduce(
    (sum, item) => sum + item.quantity * item.salePrice * (item.tax / 100),
    0
  );

  const billInfoContent = (
  <>
    <div>
      <p>
        <strong>Bill No:</strong> {formatBillNumber ? formatBillNumber(returnData) : billNumber}
      </p>
      <p>
        <strong>{type === "SupplierReturn" ? "Supplier Name" : type === "Wastage" ? "Source Name" : "Customer Name"}:</strong>{" "}
        {customerName || "N/A"}
      </p>
      <p>
        <strong>Return Date:</strong>{" "}
        {returnedAt ? format(new Date(returnedAt), "MM/dd/yyyy") : "N/A"}
      </p>
    </div>
    <div>
      <p>
        <strong>Type:</strong>{" "}
        {type ? type.replace("Return", "").charAt(0).toUpperCase() + type.replace("Return", "").slice(1) : "N/A"}
      </p>
      <p>
        <strong>Adjustment Type:</strong>{" "}
        {adjustmentType ? adjustmentType.charAt(0).toUpperCase() + adjustmentType.slice(1) : "N/A"}
      </p>
    </div>
  </>
);

  const tableContent = (
    <>
      <thead>
        <tr>
          <th>Sl No</th>
          <th>Medicine Name</th>
          <th>Quantity</th>
          <th>Unit Price (৳)</th>
          <th>Tax (%)</th>
          <th>Total (৳)</th>
        </tr>
      </thead>
      <tbody>
        {returnItems.length > 0 ? (
          returnItems.map((item, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td>{item.medicineName}</td>
              <td>{item.quantity}</td>
              <td>৳ {item.salePrice.toFixed(2)}</td>
              <td>{item.tax}</td>
              <td>৳ {item.total}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="6" style={{ textAlign: "center" }}>
              No items available
            </td>
          </tr>
        )}
      </tbody>
    </>
  );

  const summaryContent = (
    <>
      <div>
        <span>Sub Total</span>
        <span>৳ {subTotal.toFixed(2)}</span>
      </div>
      <div>
        <span>Vat</span>
        <span>৳ {taxAmount.toFixed(2)}</span>
      </div>
      <div>
        <span>Net Total</span>
        <span>৳ {totalAmount}</span>
      </div>
    </>
  );

  const noteContent = note && (
    <p className="note-box">
      <strong>Note:</strong> {note}
    </p>
  );

  const handlePrint = () => {
    const elementsToHide = document.querySelectorAll(".print-hidden");
    elementsToHide.forEach((el) => (el.style.display = "none"));

    const printLayout = document.querySelector(".print-layout");
    if (printLayout) {
      printLayout.style.display = "block";
    }

    setTimeout(() => {
      window.print();
      setTimeout(() => {
        elementsToHide.forEach((el) => (el.style.display = ""));
        if (printLayout) {
          printLayout.style.display = "none";
        }
      }, 500);
    }, 1000);
  };
  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = (ids) => {
    onDelete(ids, type);
    setIsDeleteModalOpen(false);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 overflow-y-auto print-hidden">
        <div className="bg-white shadow-lg w-full my-4 sm:my-0 max-h-[100vh] h-full overflow-y-auto rounded-lg">
          {/* Modal Header */}
          <div className="flex items-center justify-between gap-3 flex-wrap bg-gray-200 border-b border-dashed border-gray-300 p-4 print-hidden">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Return Details - {type.replace("Return", "")}
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handlePrint}
                className="size-7 rounded-full bg-white text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                title="Print"
              >
                <FaPrint />
              </button>
              <button
                onClick={handleDelete}
                className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                title="Delete"
              >
                <RiDeleteBin6Line />
              </button>
              <button
                onClick={onClose}
                className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                title="Close"
              >
                <HiXMark />
              </button>
            </div>
          </div>

          <div className="px-4 print-hidden">
            <div className="mt-4 sm:flex justify-between items-start mb-2">
              <div>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Bill No </span>:
                  <span className="text-gray-600 pl-1">
                    {formatBillNumber ? formatBillNumber(returnData) : billNumber}
                  </span>
                </p>
                <p className="text-[15px] text-black mb-1">
                  <span className="w-[120px] inline-block">
                    {type === "SupplierReturn" ? "Supplier Name" : "Customer Name"}
                  </span>:
                  <span className="text-gray-600 pl-1">{customerName || "N/A"}</span>
                </p>
              </div>
              <div>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Return Date</span>:
                  <span className="text-gray-600 pl-1">
                    {returnedAt ? format(new Date(returnedAt), "MM/dd/yyyy") : "N/A"}
                  </span>
                </p>
                <p className="text-[15px] text-black mb-1">
                  <span className="w-[120px] inline-block">Type</span>:
                  <span className="text-gray-600 capitalize pl-1">
                    {type.replace("Return", "") || "N/A"}
                  </span>
                </p>
                <p className="text-[15px] text-black mb-1">
                  <span className="w-[120px] inline-block">Adjustment Type</span>:
                  <span className="text-gray-600 capitalize pl-1">
                    {adjustmentType || "N/A"}
                  </span>
                </p>
              </div>
            </div>

            {returnItems.length > 0 && (
              <div className="mt-4 sm:mt-6">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-xs sm:text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-2 sm:p-3 border font-semibold">Medicine Name</th>
                        <th className="p-2 sm:p-3 border font-semibold">Quantity</th>
                        <th className="p-2 sm:p-3 border font-semibold">Unit Price (৳)</th>
                        <th className="p-2 sm:p-3 border font-semibold">Vat (%)</th>
                        <th className="p-2 sm:p-3 border font-semibold">Total (৳)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {returnItems.map((item, index) => (
                        <tr key={index}>
                          <td className="p-2 sm:p-3 border text-center">{item.medicineName}</td>
                          <td className="p-2 sm:p-3 border text-center">{item.quantity}</td>
                          <td className="p-2 sm:p-3 border text-center">{item.salePrice.toFixed(2)}</td>
                          <td className="p-2 sm:p-3 border text-center">{item.tax}</td>
                          <td className="p-2 sm:p-3 border text-center">{item.total}</td>
                        </tr>
                      ))}
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3 pt-4"
                          colSpan="4"
                        >
                          <p><strong>Sub Total (৳) :</strong></p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{subTotal.toFixed(2)}</p>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3"
                          colSpan="4"
                        >
                          <p><strong>Vat (৳) :</strong></p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{taxAmount.toFixed(2)}</p>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800"
                          colSpan="4"
                        >
                          <strong className="py-1 px-3 text-sm">Net Total (৳) :</strong>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800">
                          <strong className="py-1 px-3 border-t border-gray-400/20 block">
                            {totalAmount}
                          </strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {note && (
              <p className="text-[15px] text-black mb-1 max-w-[600px] mt-4">
                <span className="inline-block mr-1 capitalize font-inter text-M-heading-color font-semibold">
                  Note:{" "}
                </span>
                <span className="text-M-text-color">{note}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      <PrintLayout
        className="print-layout"
        billInfo={billInfoContent}
        table={tableContent}
        summary={summaryContent}
        note={noteContent}
        title={`${type.replace("Return", "")}`}
      />

      <ConfirmActionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        ids={id}
        entityName="return"
        title="Confirm Deletion"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default ViewReturnModal;
